import { useCallback, useEffect, useState } from 'react'
import { farmerSaleRepository } from '../../data/repositories/FarmerSaleRepositoryImpl'
import { FarmerSaleRecord } from '../../domain/entities/FarmerSale'
import { HierarchyOption } from '../../domain/repositories/FarmerSaleRepository'

export const useFarmerSales = (initialLimit = 10) => {
  const [records, setRecords] = useState<FarmerSaleRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [paymentMode, setPaymentMode] = useState<string | undefined>(undefined)
  const [fpoId, setFpoIdState] = useState<string | undefined>(undefined)
  const [retailerId, setRetailerId] = useState<string | undefined>(undefined)
  const [vlccId, setVlccId] = useState<string | undefined>(undefined)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  const [fpoOptions, setFpoOptions] = useState<HierarchyOption[]>([])
  const [retailerOptions, setRetailerOptions] = useState<HierarchyOption[]>([])
  const [vlccOptions, setVlccOptions] = useState<HierarchyOption[]>([])

  // Selecting an FPO narrows the Retailer and VLCC lists, and clears any previous selections.
  const setFpoId = useCallback((value: string | undefined) => {
    setFpoIdState(value)
    setRetailerId(undefined)
    setVlccId(undefined)
  }, [])

  const fetchSales = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await farmerSaleRepository.getFarmerSales({
        search: search || undefined,
        paymentMode,
        fpoId,
        retailerId,
        vlccId,
        page,
        limit: initialLimit,
      })
      setRecords(res.records)
      setTotalPages(res.totalPages)
      setTotalItems(res.totalItems)
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to load farmer sales.')
      setRecords([])
      setTotalPages(1)
      setTotalItems(0)
    } finally {
      setLoading(false)
    }
  }, [search, paymentMode, fpoId, retailerId, vlccId, page, initialLimit])

  useEffect(() => {
    fetchSales()
  }, [fetchSales])

  useEffect(() => {
    let active = true
    farmerSaleRepository
      .getFpoOptions()
      .then((data) => {
        if (active) setFpoOptions(data)
      })
      .catch(() => {
        if (active) setFpoOptions([])
      })
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    let active = true
    farmerSaleRepository
      .getRetailerOptions(fpoId)
      .then((data) => {
        if (active) setRetailerOptions(data)
      })
      .catch(() => {
        if (active) setRetailerOptions([])
      })
    return () => {
      active = false
    }
  }, [fpoId])

  useEffect(() => {
    let active = true
    farmerSaleRepository
      .getVlccOptions(fpoId)
      .then((data) => {
        if (active) setVlccOptions(data)
      })
      .catch(() => {
        if (active) setVlccOptions([])
      })
    return () => {
      active = false
    }
  }, [fpoId])

  const fetchFarmerDetails = async (farmerId: string) => {
    setLoading(true)
    setError(null)
    try {
      const res = await farmerSaleRepository.getFarmerDetails(farmerId)
      return res
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to load farmer sales details.')
      return []
    } finally {
      setLoading(false)
    }
  }

  const resetFilters = () => {
    setSearch('')
    setPaymentMode(undefined)
    setFpoIdState(undefined)
    setRetailerId(undefined)
    setVlccId(undefined)
    setPage(1)
  }

  return {
    records,
    loading,
    error,
    search,
    setSearch,
    paymentMode,
    setPaymentMode,
    fpoId,
    setFpoId,
    retailerId,
    setRetailerId,
    vlccId,
    setVlccId,
    fpoOptions,
    retailerOptions,
    vlccOptions,
    page,
    setPage,
    totalPages,
    totalItems,
    fetchFarmerDetails,
    resetFilters,
    refresh: fetchSales,
  }
}
